import React, { useContext, useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";

import {
  TextField,
  InputAdornment,
  MenuItem,
  Tooltip
} from '@mui/material';

import ProjectContext from '../../contexts/ProjectContext';
import {useFetch} from '../../components/auth/useFetch';

export default function FormField(props) {
  const { isAuthenticated } = useAuth0();
  const projectContext = useContext(ProjectContext);

  const { name, required, type, selectTypeVariant, groupSize, dataSuperset, validationType, placeholder, label, units, disabled, defaultValue, updateParentStateRecord, dependsOn, value, validateAnchorAgainst, helperTextDefault, toolTip, customOptions } = props
  const [selectItems, setSelectItems] = useState([]);
  const [finalDefaultValue, setFinalDefaultValue] = useState(defaultValue);
  const [firstLoad, setFirstLoad] = useState(true)
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState(helperTextDefault);
  const fetchWithAuth = useFetch()

  let response;
  let result;
  let resultList;


  useEffect(() => {
    isAuthenticated && (() => {
       fetchSelectLists()
    })()
}, [isAuthenticated])

  useEffect(()=>{
    fetchSelectLists()
  },[customOptions])

/*
### COMPONENT DOCUMENTATION ##############
Props
- name: field name
- required: is this field required? Enforced by HTML form.
- label: field label, rendered in DOM
- type: determines freetext or 'select'/dropdown. If blank, select=false, if 'select', select=true
- selectTypeVariant: determines type of data to populate select list with. Single word 'manf', 'camera', or 'res'
- groupSize: determines if this field should be rendered as single field at full width (value 1), as or as a group of fields on the same line, collectively taking up the full width
- dataSuperset: determines the dataset that this field should rely on. Used for passing information to backend DB calls.
- validationType: frontend validation. Is this field a number, a decimal, an ID with specific character requirements?
- placeholder: placeholder string for text field.
- units: a string that indicates units on the DOM. Eg. 'mm', 'px'.
- disabled: is this field disabled?
- defaultValue: do we have an INITIAL default value? Used where a non-entry will break the FDL generator, and where we will have a common standard value. Eg. Anamorphic 1
-- Also used where 'undefined' may cause the React engine to throw a warning (eg, select lists)
- updateParentStateRecord: the state variable on the overall form component or page that should be set when this field has data entered. Useful for informing the parent of state changes.
- dependsOn: an object containing a list of form fields that this field depends on. If the passed form fields contain data, this field will be enabled. Otherwise disabled.
- value: used to set the value of the form field at any time during user interaction. Eg. use the Resolution dropdown selection to automatically fill the Resolution X and Resolution Y text fields.

##########################################
*/



  // Manually assign 'value' field to defaultValue ONLY on initial component load
  // After initial component load, use the value provided by the user OR assigned by code directly to 'value' (eg., when autofilling resolution x and y)
  // This ensures we have values set in the edit modal while allowing alterations to this value
  let myValue;
  if(firstLoad){
    myValue = defaultValue;
    setFirstLoad(false)
  }else{
    myValue = value;
  }


  // If an instance of this component is set to disabled, use this setting
  let isDisabled = disabled;
  // Iterate over all items defined in dependsOn array, if present
  // If any items do not have a value (field is not set), quit
  // Otherwise, we know all dependedOn fields are filled, so we can enable THIS field
  if (dependsOn != undefined) {
    isDisabled = false;
    for(let item in dependsOn){
      if(dependsOn[item] !== undefined){

        if (dependsOn[item].length == 0) {
          isDisabled = true;
          myValue = '';
          break;
        }
      }
    }
  }


  // Assign unit type from instance
  let unitType = '';
  if (units != null) {
    unitType = units
  }

  // Assign placeholder text from instance
  let placeholderText = '';
  if (placeholder !== null) {
    placeholderText = placeholder;
  }

  // Set width of field depending on whether it's a single entry (a label) or one of a pair of entries (resolution x and y)
  // TODO: Let's do a switch statement here. What are we neanderthals?
  let fieldWidth = '100%'
  if (groupSize === 2) {
    fieldWidth = '40%'
  }
  if (groupSize == 3){
    fieldWidth = '25%'
  }

  // Assign type as select from instance
  let select = false;
  if (type === 'select') {
    select = true;
  }

  // Styling (additional styling in render function under 'sx' property)
  let formFieldStyle = {
    backgroundColor: 'rgba(72, 110, 84, 0.0)',
    opacity: isDisabled ? '0.3' : '1',
    margin: '0 10px 15px 0',
    width: fieldWidth,
    height: 'auto'
  };




  // ##### Get data to populate the dropdown select lists #####
  // Use Effect, call an async function to run before component is rendered
  useEffect(() => {
    if (type === 'select') {
      fetchSelectLists();
    }
  }, [dataSuperset]);

  // Nested async function called before component is rendered
  // Sets the state of selectItems array once the data has been fetched,
  // which forces a component rerender with the correct data inside
  const fetchSelectLists = async () => {
    

    // Update the parent's record of what this field holds, so that other fields that rely on this one
    // can have up to date data
    if(defaultValue != undefined && updateParentStateRecord != null){
      updateParentStateRecord(defaultValue)
    }


      let list = [];
      let fetchDetails;

      switch (selectTypeVariant) {
        case 'manf':
          fetchDetails = {
            method: "POST",
            body: JSON.stringify({'dataType': 'manf'}),
            headers: { "Content-type": "application/json; charset=UTF-8", 
            'X-Project-ID': projectContext?.project_db }
          };

          response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/fdlgetcameradata/`, 
          fetchDetails
          )
          result = await response.json()
          resultList = result['GET']
          if (resultList != undefined){
            for (let i = 0; i < resultList.length; i++) {
              list.push(resultList[i])
            }
          }
          
          break;

        case 'camera':
          if (dataSuperset != ''){
            fetchDetails = {
              method: "POST",
              body: JSON.stringify({'dataType': 'camera', 'dataSuperset': dataSuperset}),
              headers: { "Content-type": "application/json; charset=UTF-8", 'X-Project-ID': projectContext?.project_db }
            };
            response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/fdlgetcameradata/`, fetchDetails);
            result = await response.json()
            list = result['GET'];
          }
          break;

        case 'res':
          if (dataSuperset != ''){
            fetchDetails = {
              method: "POST",
              body: JSON.stringify({'dataType': 'res', 'dataSuperset': dataSuperset}),
              headers: { "Content-type": "application/json; charset=UTF-8", 'X-Project-ID': projectContext?.project_db}
            };
            response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/fdlgetcameradata/`, fetchDetails);
            result = await response.json()
            list = result['GET'];
          }
          break;

        case 'custom':
          list = [...customOptions]
          break;

        default:
          list = ['Error']
      }
      // Remove duplicates from list (don't trust the source data is perfect)
      // Convert the array to a set to automatically removed duplicate values, then convert back to an array
      list = new Set(list);
      list = [...list];
      if (list.length != 0){
        setSelectItems(list);
        setFinalDefaultValue(defaultValue)
      } 
    
  }

  useEffect(()=>{
    setFinalDefaultValue(defaultValue);
  },[selectItems])

  // ##### Inform parent component of state changes #####
  // When the value of the input field changes,
  // call the setState function in the parent that has been passed to this component
  // under the name 'updateParentStateRecord
  // to update the parent component's state value for this field
  function handleChange(event) {
    // If this component hasn't been initialised with an updateParentStateRecord prop,
    // do nothing. Otherwise, call the function
    if (updateParentStateRecord != null) {
      updateParentStateRecord(event.target.value)
    }
  
  }


  // Is called on every keyUp event
  function inputValidation(event) {
    if (validationType === 'id') {
      if (event.target.value.search(/[^A-Za-z0-9_]+/) !== -1) {
        setError(true);
        setHelperText('Invalid - enter only alphanumeric characters and underscores');
      }
      else if (event.target.value.length > 32) {
        setError(true);
        setHelperText('Invalid - max ID length 32 characters');
      }
      else {
        setError(false);
        setHelperText(helperTextDefault);
      }
    }

    if (validationType === 'decimal') {
      if (event.target.value.search(/[^0-9.]+/) !== -1) {
        setError(true);
        setHelperText('Invalid - enter a decimal number');
      }
      else {
        setError(false);
        setHelperText(helperTextDefault);
      }
    }

    if (validationType === 'number') {
      if (event.target.value.search(/[^0-9]+/) !== -1) {
        setError(true);
        setHelperText('Invalid - enter a whole number');
      }
      else {
        setError(false);
        setHelperText(helperTextDefault);
      }
    }

    if (validationType === 'protection') {
      if (event.target.value.search(/[^0-9.]+/) !== -1) {
        setError(true);
        setHelperText('Invalid - enter a decimal number');
      }
      else if (event.target.value > 1){
        setError(true);
        setHelperText('Invalid - protection value must be less than 1');
      }
      else {
        setError(false);
        setHelperText(helperTextDefault);
      }
    }

    // Validate anchor - calculate boundaries using validateAgainst prop
    // validateAgainst values for validating Effective Resolution Anchor X = {'effectiveX': effectiveX, 'resolutionX': resolutionX,}
    if (validationType === 'anchor'){
      // if anchor x + effective res x > full res, validation fails
      if((parseFloat(event.target.value) + parseFloat(validateAnchorAgainst['a'])) > parseFloat(validateAnchorAgainst['b'])){
        setError(true);
        setHelperText('Invalid - value too large, will push effective canvas out of bounds')
      }else{
        setError(false);
        setHelperText(' ')
      }
    }


  }


 

  

  
  return (
    <Tooltip title={toolTip} placement='top-start' arrow enterDelay={1000} >
      <TextField
        name={name}
        error={error}
        helperText={helperText}
        onKeyUp={inputValidation}
        style={formFieldStyle}
        label={label}
        select={select}
        type={type}
        InputProps={{
          startAdornment: <InputAdornment position="start">{unitType}</InputAdornment>,
        }}
        required={required}
        placeholder={placeholderText}
        disabled={isDisabled}
        defaultValue={finalDefaultValue}
        value={myValue}
        onChange={handleChange}
        sx={{
          '& .MuiFormLabel-root': {
            color: 'silver'
          },
          '& .MuiOutlinedInput-input': {
            color: 'rgb(85, 220, 50)'
          },
          '& .MuiOutlinedInput-input::placeholder': {
            color: 'rgb(85, 140, 50)'
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(135, 178, 123, 0.17)'
          },
            '& .MuiFormHelperText-root': {
              marginTop:'0px',
              color: error ? 'red' : 'grey'
          }
        }}
      >

        {selectItems.map((item) => (
          <MenuItem key={item} value={item}>{item}</MenuItem>
        ))}

      </TextField>
    </Tooltip>
  );
}